import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Activity, Heart, HeartPulse, Wind, Droplet, Sparkles, Waves, CheckCircle2, Info } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Reacciones corporales al ESTRÉS (Situación 1A)
const STRESS_REACTIONS = [
  { icon: HeartPulse, title: "Aumento del ritmo cardíaco", body: "El corazón late más rápido, preparando al cuerpo para la respuesta de \"lucha o huida\"." },
  { icon: Activity, title: "Tensión muscular", body: "Los músculos se tensan, especialmente cuello, hombros y espalda, como respuesta natural al estrés." },
  { icon: Wind, title: "Respiración acelerada", body: "La respiración se vuelve rápida y superficial, pudiendo provocar hiperventilación y sensación de falta de aire." },
  { icon: Droplet, title: "Sudoración", body: "El cuerpo empieza a sudar más, incluso sin calor, como forma de regular la temperatura corporal." },
  { icon: Waves, title: "Problemas digestivos", body: "Malestar estomacal, náuseas y otros problemas debido a la activación del sistema nervioso simpático." },
  { icon: Sparkles, title: "Aumento del cortisol", body: "Los niveles de cortisol (hormona del estrés) suben; mantenerlos altos afecta la salud a largo plazo." },
];

// Reacciones corporales a la CALMA (Situación 1B)
const CALM_REACTIONS = [
  { icon: Heart, title: "Ritmo cardíaco regular", body: "El corazón mantiene un ritmo normal y relajado, reduciendo el riesgo cardiovascular." },
  { icon: Activity, title: "Relajación muscular", body: "Los músculos permanecen relajados, mejorando la circulación y reduciendo dolores y fatiga." },
  { icon: Wind, title: "Respiración estable", body: "Respiración estable y profunda que reduce la activación del sistema nervioso simpático." },
  { icon: Droplet, title: "Menor sudoración", body: "La sudoración se mantiene en niveles normales; el cuerpo no percibe amenaza." },
  { icon: Waves, title: "Digestión normal", body: "El sistema digestivo funciona correctamente, sin malestares ni náuseas." },
  { icon: Sparkles, title: "Cortisol en niveles normales", body: "Favorece la salud a largo plazo y reduce el riesgo de hipertensión y enfermedades relacionadas con el estrés." },
];

function ScenarioCard({ variant, label, thought, expected, answered, onAnswer, reactions }) {
  const isStress = variant === "stress";
  const bandColor = isStress ? "var(--ha-secondary)" : "var(--ha-primary)";

  return (
    <Card className="rounded-3xl border-[var(--ha-border)] bg-white overflow-hidden" data-testid={`abc-phys-scenario-${variant}`}>
      <div className="h-1.5" style={{ backgroundColor: bandColor }} />
      <CardContent className="p-6 sm:p-8">
        <div className="flex items-center gap-2">
          <span
            className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] text-white"
            style={{ backgroundColor: bandColor }}
          >
            {label}
          </span>
        </div>

        <div className="mt-4 space-y-3">
          <div className="rounded-2xl border border-[var(--ha-border)] p-4">
            <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold">Evento (A)</p>
            <p className="mt-1 text-[var(--ha-text)]">Estás en un atasco y vas a llegar tarde a un evento.</p>
          </div>
          <div className="rounded-2xl border border-[var(--ha-border)] p-4">
            <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold">Pensamiento (B)</p>
            <p className="mt-1 italic text-[var(--ha-text)]">"{thought}"</p>
          </div>
        </div>

        {!answered && (
          <div className="mt-5">
            <p className="font-semibold text-[var(--ha-text)]">¿Qué sentirías si te vieras en esta situación?</p>
            <div className="mt-3 flex flex-wrap gap-3">
              <Button
                data-testid={`abc-phys-${variant}-answer-estres`}
                variant="outline"
                onClick={() => onAnswer("estres")}
                className="rounded-full h-11 px-6 border-[var(--ha-border)] hover:bg-[var(--ha-secondary)]/10"
              >
                Estrés
              </Button>
              <Button
                data-testid={`abc-phys-${variant}-answer-calma`}
                variant="outline"
                onClick={() => onAnswer("calma")}
                className="rounded-full h-11 px-6 border-[var(--ha-border)] hover:bg-[var(--ha-primary)]/10"
              >
                Calma
              </Button>
            </div>
          </div>
        )}

        {answered && (
          <div className="mt-6 space-y-4" data-testid={`abc-phys-${variant}-reveal`}>
            <div
              className="rounded-2xl p-4 flex items-start gap-3"
              style={{ backgroundColor: `${bandColor}12`, border: `1px solid ${bandColor}40` }}
            >
              {answered === expected ? (
                <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" style={{ color: bandColor }} strokeWidth={1.75} />
              ) : (
                <Info className="w-5 h-5 mt-0.5 shrink-0" style={{ color: bandColor }} strokeWidth={1.75} />
              )}
              <p className="text-sm text-[var(--ha-text)] leading-relaxed">
                {isStress
                  ? "La mayoría de las personas se sentirían estresadas. Con este pensamiento, a nivel fisiológico tu cuerpo reaccionaría así:"
                  : "La mayoría de las personas se sentirían calmadas, o al menos no estresadas. Con este pensamiento, a nivel fisiológico tu cuerpo reaccionaría así:"}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              {reactions.map((r, i) => {
                const Icon = r.icon;
                return (
                  <div
                    key={i}
                    className="rounded-2xl border border-[var(--ha-border)] bg-white p-4 flex gap-3"
                    data-testid={`abc-phys-${variant}-reaction-${i}`}
                  >
                    <div
                      className="h-9 w-9 rounded-xl grid place-items-center shrink-0"
                      style={{ backgroundColor: `${bandColor}18`, color: bandColor }}
                    >
                      <Icon className="w-4.5 h-4.5" strokeWidth={1.75} />
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-[var(--ha-text)]">{r.title}</p>
                      <p className="mt-1 text-xs text-[var(--ha-muted)] leading-relaxed">{r.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AbcPhysiologyStep({ onDone, sessionNumber = 1 }) {
  const [answerA, setAnswerA] = useState(null); // 1A → esperado "estres"
  const [answerB, setAnswerB] = useState(null); // 1B → esperado "calma"
  const [saving, setSaving] = useState(false);

  const bothAnswered = answerA && answerB;

  const finish = async () => {
    setSaving(true);
    try {
      await api.post("/progress", {
        session_number: sessionNumber,
        step: "abc-physiology",
        completed: true,
        payload: { answer_a: answerA, answer_b: answerB },
      });
      onDone();
    } catch {
      toast.error("No pudimos guardar el ejercicio.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="abc-physiology-step">
      <h2 className="text-2xl sm:text-3xl font-bold text-[var(--ha-text)]">Cómo los pensamientos afectan a tu cuerpo</h2>
      <p className="mt-3 text-[var(--ha-muted)] leading-relaxed max-w-2xl">
        Nuestros pensamientos no solo influyen en cómo nos sentimos, también en cómo <strong>reacciona nuestro cuerpo</strong>.
        La misma situación puede generar reacciones fisiológicas muy distintas dependiendo de cómo la interpretemos.
      </p>

      <div className="mt-8 space-y-5">
        <ScenarioCard
          variant="stress"
          label="Situación 1A · Pensamiento negativo"
          thought="¡Oh no, voy a perder la cita!"
          expected="estres"
          answered={answerA}
          onAnswer={setAnswerA}
          reactions={STRESS_REACTIONS}
        />

        <ScenarioCard
          variant="calm"
          label="Situación 1B · Pensamiento realista"
          thought="Estoy en este atasco pero parece que va mejorando el tráfico. Voy a avisar de que puede que llegue un poco tarde."
          expected="calma"
          answered={answerB}
          onAnswer={setAnswerB}
          reactions={CALM_REACTIONS}
        />

        {bothAnswered && (
          <div className="rounded-2xl border border-[var(--ha-primary)]/30 bg-[var(--ha-primary)]/5 p-5" data-testid="abc-phys-takeaway">
            <p className="text-xs uppercase tracking-[0.18em] font-bold text-[var(--ha-primary)]">Para recordar</p>
            <p className="mt-2 text-[var(--ha-text)] leading-relaxed">
              Aplicar pensamientos más realistas o positivos a situaciones cotidianas puede transformar la manera en que
              experimentamos y respondemos a esos eventos, generando emociones más constructivas y beneficios
              significativos para tu <strong>salud física</strong>.
            </p>
          </div>
        )}
      </div>

      <div className="mt-8 flex justify-end">
        <Button
          data-testid="abc-phys-continue"
          onClick={finish}
          disabled={!bothAnswered || saving}
          className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 px-6 disabled:opacity-50"
        >
          Continuar <ArrowRight className="w-4 h-4 ml-1.5" strokeWidth={1.75} />
        </Button>
      </div>
    </div>
  );
}
